import type { ThemeMode, TimerSettings } from '../types/timer';
import { apiJson } from './api';
import { getSession } from './auth';
import { getVisitorId } from './visitor';

export interface UiSettingsRemote {
  theme?: ThemeMode;
  auto_advance?: boolean;
  sound_enabled?: boolean;
  notifications_enabled?: boolean;
  sound_style?: 'smooth' | 'legacy';
}

export interface RemotePreferences {
  focus_duration: number;
  short_break_duration: number;
  long_break_duration: number;
  cycle_count: number;
  custom_sounds: Record<string, string>;
  ui_settings?: UiSettingsRemote;
}

export interface ProfileResponse extends RemotePreferences {
  id: string;
  email: string | null;
  isPremium: boolean;
}

export function remoteToTimerSettings(remote: RemotePreferences): Partial<TimerSettings> {
  const settings: Partial<TimerSettings> = {
    focusDuration: remote.focus_duration,
    shortBreakDuration: remote.short_break_duration,
    longBreakDuration: remote.long_break_duration,
    cycleCount: remote.cycle_count,
    customSounds: remote.custom_sounds ?? {},
  };

  if (remote.ui_settings?.theme) {
    settings.theme = remote.ui_settings.theme;
  }
  if (remote.ui_settings?.auto_advance !== undefined) {
    settings.autoAdvance = remote.ui_settings.auto_advance;
  }
  if (remote.ui_settings?.sound_enabled !== undefined) {
    settings.soundEnabled = remote.ui_settings.sound_enabled;
  }
  if (remote.ui_settings?.sound_style !== undefined) {
    settings.soundStyle = remote.ui_settings.sound_style;
  }
  if (remote.ui_settings?.notifications_enabled !== undefined) {
    settings.notificationsEnabled = remote.ui_settings.notifications_enabled;
  }

  return settings;
}

export function timerSettingsToRemote(
  settings: Partial<TimerSettings>,
): Partial<RemotePreferences> {
  const remote: Partial<RemotePreferences> = {};
  const uiSettings: UiSettingsRemote = {};

  if (settings.focusDuration !== undefined) {
    remote.focus_duration = settings.focusDuration;
  }
  if (settings.shortBreakDuration !== undefined) {
    remote.short_break_duration = settings.shortBreakDuration;
  }
  if (settings.longBreakDuration !== undefined) {
    remote.long_break_duration = settings.longBreakDuration;
  }
  if (settings.cycleCount !== undefined) {
    remote.cycle_count = settings.cycleCount;
  }
  if (settings.customSounds !== undefined) {
    remote.custom_sounds = settings.customSounds;
  }
  if (settings.theme !== undefined) {
    uiSettings.theme = settings.theme;
  }
  if (settings.autoAdvance !== undefined) {
    uiSettings.auto_advance = settings.autoAdvance;
  }
  if (settings.soundEnabled !== undefined) {
    uiSettings.sound_enabled = settings.soundEnabled;
  }
  if (settings.notificationsEnabled !== undefined) {
    uiSettings.notifications_enabled = settings.notificationsEnabled;
  }
  if (settings.soundStyle !== undefined) {
    uiSettings.sound_style = settings.soundStyle;
  }

  if (Object.keys(uiSettings).length > 0) {
    remote.ui_settings = uiSettings;
  }

  return remote;
}

export async function fetchProfile(): Promise<ProfileResponse> {
  return apiJson<ProfileResponse>('/api/profile/');
}

export async function saveProfilePreferences(
  settings: Partial<TimerSettings>,
): Promise<void> {
  await apiJson('/api/profile/', {
    method: 'PUT',
    body: JSON.stringify(timerSettingsToRemote(settings)),
  });
}

export async function fetchVisitorPreferences(
  visitorId = getVisitorId(),
): Promise<RemotePreferences & { visitor_id: string }> {
  return apiJson(`/api/preferences/${encodeURIComponent(visitorId)}`);
}

export async function saveVisitorPreferences(
  settings: Partial<TimerSettings>,
  visitorId = getVisitorId(),
): Promise<void> {
  await apiJson(`/api/preferences/${encodeURIComponent(visitorId)}`, {
    method: 'PUT',
    body: JSON.stringify(timerSettingsToRemote(settings)),
  });
}

export async function checkPremiumStatus(
  visitorId = getVisitorId(),
): Promise<{ isPremium: boolean; userId?: string }> {
  return apiJson('/api/premium/status', {
    method: 'POST',
    body: JSON.stringify({ visitor_id: visitorId }),
  });
}

export async function loadRemotePreferences(): Promise<{
  isPremium: boolean;
  settings: Partial<TimerSettings>;
}> {
  const session = getSession();

  if (session) {
    const profile = await fetchProfile();
    return {
      isPremium: profile.isPremium,
      settings: remoteToTimerSettings(profile),
    };
  }

  const [premium, prefs] = await Promise.all([
    checkPremiumStatus(),
    fetchVisitorPreferences(),
  ]);

  return {
    isPremium: premium.isPremium,
    settings: remoteToTimerSettings(prefs),
  };
}

export async function saveRemotePreferences(
  settings: Partial<TimerSettings>,
): Promise<void> {
  if (getSession()) {
    await saveProfilePreferences(settings);
    return;
  }

  await saveVisitorPreferences(settings);
}
